import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useVolunteerKyc() {
  const { api, AdminId } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [kycStatusLoading, setKycStatusLoading] = useState(false);
  const [volunteerKyc, setVolunteerKyc] = useState([]);
  const [volunteerKycList, setVolunteerKycList] = useState([]);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getVolunteerUserId = () => {
    try {
      const volunteerData = JSON.parse(
        sessionStorage.getItem("volunteerData") || "{}"
      );

      return String(
        volunteerData?.userId ||
          volunteerData?.userid ||
          volunteerData?.id ||
          AdminId ||
          ""
      );
    } catch (error) {
      return String(AdminId || "");
    }
  };

  /* ---------------- FETCH VOLUNTEER KYC DETAILS ---------------- */

  const fetchVolunteerKycDetails = useCallback(
    async (volunteerId, preserveLocalUpdates = true) => {
      if (!volunteerId) return null;

      setLoading(true);

      try {
        const body = {
          adminId: getVolunteerUserId(),
          volunteerId: String(volunteerId || ""),
        };

        const res = await api.post(
          "/api/VolunteerUser/v1.0/getVolunteerKYCDetails",
          body
        );

        if (res?.data?.responseCode === "00") {
          const serverData = res.data.data || null;

          if (!serverData) {
            setVolunteerKyc(null);
            return null;
          }

          if (preserveLocalUpdates) {
            setVolunteerKyc((prev) => {
              if (
                prev &&
                ["Approved", "Rejected"].includes(prev.status) &&
                serverData.status === "Pending"
              ) {
                return {
                  ...serverData,
                  status: prev.status,
                };
              }

              return serverData;
            });
          } else {
            setVolunteerKyc(serverData);
          }

          return serverData;
        }

        if (shouldShowLocalError(res?.data)) {
          toast.error(
            res?.data?.responseMessage || "Failed to fetch Volunteer KYC details"
          );
        }

        setVolunteerKyc(null);
        return null;
      } catch (err) {
        console.error("fetchVolunteerKycDetails error:", err);
        toast.error("Error fetching Volunteer KYC details");
        setVolunteerKyc(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, AdminId]
  );

  /* ---------------- ADD / SUBMIT VOLUNTEER KYC STEP ---------------- */

  const submitVolunteerKycStep = useCallback(
    async (stepNumber, formData) => {
      setLoading(true);

      try {
        const payload = {
          adminId: getVolunteerUserId(),
          volunteerId: String(
            formData?.volunteer_code ||
              formData?.step1?.volunteer_code ||
              formData?.step2?.volunteer_code ||
              formData?.step3?.volunteer_code ||
              formData?.step4?.volunteer_code ||
              ""
          ),
          step: String(stepNumber || ""),
        };

        if (stepNumber === 1) {
          payload.step1 = {
            city: formData?.step1?.city || "",
            area: formData?.step1?.area || "",
          };
        }

        if (stepNumber === 2) {
          payload.step2 = {
            profilePicture: formData?.step2?.profilePicture || "",
          };
        }

        if (stepNumber === 3) {
          payload.step3 = {
            aadharNumber: formData?.step3?.aadharNumber || "",
            aadharFrontPhoto: formData?.step3?.aadharFrontPhoto || "",
            aadharBackPhoto: formData?.step3?.aadharBackPhoto || "",
          };
        }

        if (stepNumber === 4) {
          payload.step4 = {
            panCard: formData?.step4?.panCard || "",
            panCardFrontPhoto: formData?.step4?.panCardFrontPhoto || "",
          };
        }

        console.log("FINAL VOLUNTEER KYC PAYLOAD:", payload);

        const res = await api.post(
          "/api/VolunteerUser/v1.0/addVolunteerKYCDetails",
          payload
        );

        if (res?.data?.responseCode === "00") {
          toast.success(res?.data?.responseMessage || "KYC details saved");
          return true;
        }

        if (shouldShowLocalError(res?.data)) {
          toast.error(res?.data?.responseMessage || "Failed to save KYC details");
        }

        return false;
      } catch (err) {
        console.error("submitVolunteerKycStep error:", err);
        toast.error("Error saving Volunteer KYC details");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [api, AdminId]
  );

  /* ---------------- VERIFY / VIEW VOLUNTEER KYC ---------------- */

  const handleVolunteerKycAction = async (
    kycId,
    action = "view",
    status = ""
  ) => {
    setKycStatusLoading(true);

    try {
      const payload = {
        kycId: String(kycId || ""),
        kycStatus: action === "view" ? "" : String(status || ""),
        adminId: getVolunteerUserId(),
      };

      console.log("VERIFY VOLUNTEER KYC PAYLOAD:", payload);

      const res = await api.post(
        "/api/VolunteerAdmin/v1.0/verifyVolunteeerkycDetails",
        payload
      );

      if (res?.data?.responseCode === "00") {
        if (action === "view") {
          return res?.data?.data;
        }

        toast.success(res?.data?.responseMessage || "KYC status updated");

        return {
          success: true,
          message: res?.data?.responseMessage,
        };
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Volunteer KYC action failed");
      }

      return action === "view" ? null : { success: false };
    } catch (err) {
      console.error("handleVolunteerKycAction error:", err);
      toast.error("Volunteer KYC action failed");
      return action === "view" ? null : { success: false };
    } finally {
      setKycStatusLoading(false);
    }
  };

  /* ---------------- FETCH ALL VOLUNTEER KYC DETAILS ---------------- */

  const fetchAllVolunteerKycDetails = useCallback(async () => {
    setLoading(true);

    try {
      const body = {
        adminId: getVolunteerUserId(),
      };

      const res = await api.post(
        "/api/VolunteerAdmin/v1.0/getAllVolunteerKYCDetails",
        body
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];

        setVolunteerKycList(list);

        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch KYC list");
      }

      setVolunteerKycList([]);
      return [];
    } catch (error) {
      console.error("fetchAllVolunteerKycDetails error:", error);
      toast.error("Error fetching KYC list");
      setVolunteerKycList([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [api, AdminId]);

  return {
    volunteerKyc,
    setVolunteerKyc,
    loading,
    kycStatusLoading,
    volunteerKycList,
    setVolunteerKycList,
    fetchVolunteerKycDetails,
    submitVolunteerKycStep,
    fetchAllVolunteerKycDetails,
    handleVolunteerKycAction,
    getVolunteerUserId,
  };
}